import { useCallback, useMemo, useRef, useState } from 'react';
import { applyTransition, fetchTransitionAdvice, type TransitionAdvice } from '../../api/endpoints';
import { ApiError, toApiError } from '../../api/errors';
import { useResource } from '../../hooks/useResource';
import { chipColours } from '../../lib/colour';
import { useTheme } from '../../theme/ThemeProvider';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { Id, Transition } from '../../api/types';
import { Popover } from '../ui/Popover';
import { Spinner } from '../ui/Skeleton';
import { buildMenu, type StatusMenu } from './status-menu';
import { StatusChip } from './StatusChip';

/**
 * 🔴 THE INLINE STATUS CONTROL — the most frequent action in the product, so it lives on the row and
 * never requires opening the ticket (README, "Status is an inline dropdown ON the row").
 *
 * 🔴🔴 IT USED TO OFFER ONLY WHAT `status_transition` LISTED, AND THAT WAS PH-19's OTHER HALF.
 *   `done` has no outgoing edges in that table, so this menu rendered *"No moves are permitted from
 *   Done. An admin can add one in Settings → Transitions."* — and a closed ticket could not be
 *   reopened from anywhere in the product. That is not a hypothetical: it is written up in
 *   `20260817-definition-of-done-review-gate.md`, where reopening PH-10 was impossible for exactly
 *   this reason. ⇒ Since 2026-08-17 any ACTIVE status is a legal target and this menu offers all of
 *   them.
 *
 * ⭐⭐ THE SUGGESTION IS NOT THROWN AWAY — it is the ORDER. `GET /tickets/:id/transitions` still says
 * what normally comes next, those statuses are listed first under a heading, and everything else
 * follows under a second one. ⛔ Nothing is disabled on account of not being suggested.
 *
 * ⭐ Four outcomes are kept apart, because they mean different things:
 *   · suggested + others  → the normal case
 *   · NO suggestions      → a real answer about SUGGESTIONS, and never a refusal to move
 *   · an ERROR            → "we do not know what is suggested" — the other statuses are still shown,
 *                           because their legality never depended on that request succeeding
 *   · vocabulary missing  → nothing to offer at all, and it says so rather than showing an empty menu
 */
export function StatusDropdown({
  ticketId,
  statusCode,
  onChanged,
  compact = false,
}: {
  ticketId: Id;
  statusCode: string;
  onChanged: () => void;
  compact?: boolean;
}) {
  const vocab = useVocabulary();
  const { resolved } = useTheme();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const status = vocab.status(statusCode);

  // Only fetched once the menu is opened: 50 rows × one transitions call each on page load would be
  // 50 requests for a menu nobody opened.
  const advice = useResource<TransitionAdvice>(
    (signal) => fetchTransitionAdvice(ticketId, signal),
    [ticketId, statusCode],
    { enabled: open },
  );

  /**
   * ⭐ SUGGESTED FIRST, THEN THE REST — built here rather than in the render so the two lists cannot
   * overlap and the current status cannot appear in either.
   * ⛔ `advisory` decides whether "the rest" is offered AT ALL. Against a deployment older than
   * PH-19 the graph is still enforced, and offering every status there would produce a menu whose
   * options mostly 409 — the pre-PH-19 defect with the sign flipped.
   */
  const menu = useMemo(
    () => buildMenu(advice.data, vocab.statuses, statusCode),
    [advice.data, vocab.statuses, statusCode],
  );

  const close = useCallback(() => setOpen(false), []);

  const choose = useCallback(
    async (toStatus: string) => {
      setSubmitting(toStatus);
      setSubmitError(null);
      try {
        await applyTransition(ticketId, toStatus);
        setSubmitting(null);
        setOpen(false);
        onChanged();
      } catch (err) {
        setSubmitting(null);
        // ⛔ The chip is NOT updated optimistically. If the API refuses the move, an optimistic chip
        // would show a status the ticket does not have — the exact "stale looks current" failure.
        setSubmitError(toApiError(err, `/tickets/${ticketId}/transition`));
      }
    },
    [ticketId, onChanged],
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Status: ${status?.label ?? statusCode}. Change status`}
        onClick={() => setOpen((value) => !value)}
        className="max-w-full cursor-pointer rounded-[3px] focus-visible:outline-2"
      >
        <StatusChip
          status={status}
          className={compact ? 'max-w-[7.5rem]' : 'max-w-[9.5rem]'}
          trailing={<span aria-hidden="true">▾</span>}
        />
      </button>

      <Popover anchorRef={buttonRef} open={open} onClose={close} label="Change status">
        <TransitionMenu
          currentLabel={status?.label ?? statusCode}
          menu={menu}
          isLoading={advice.isLoading}
          loadError={advice.error}
          onRetry={advice.reload}
          submitting={submitting}
          submitError={submitError}
          onChoose={choose}
          resolveLabel={(code) => vocab.status(code)}
          isDark={resolved === 'dark'}
        />
      </Popover>
    </>
  );
}


function TransitionMenu({
  currentLabel,
  menu,
  isLoading,
  loadError,
  onRetry,
  submitting,
  submitError,
  onChoose,
  resolveLabel,
  isDark,
}: {
  currentLabel: string;
  menu: StatusMenu;
  isLoading: boolean;
  loadError: ApiError | null;
  onRetry: () => void;
  submitting: string | null;
  submitError: ApiError | null;
  onChoose: (code: string) => void;
  resolveLabel: (code: string) => ReturnType<ReturnType<typeof useVocabulary>['status']>;
  isDark: boolean;
}) {
  const option = (transition: Transition) => {
    const target = resolveLabel(transition.to_status);
    const colours = chipColours(transition.colour ?? target?.colour, isDark);
    const busy = submitting === transition.to_status;
    return (
      <li key={transition.to_status}>
        <button
          type="button"
          disabled={submitting !== null}
          onClick={() => onChoose(transition.to_status)}
          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-black/5 disabled:opacity-60 dark:hover:bg-white/10"
        >
          <span
            style={{ backgroundColor: colours.background, color: colours.foreground }}
            className="inline-block max-w-[10rem] truncate rounded-[3px] px-1.5 py-0.5 text-[11px] font-bold tracking-wide uppercase"
          >
            {transition.label ?? target?.label ?? transition.to_status}
          </span>
          {transition.note ? (
            <span className="truncate text-xs text-[color:var(--color-ink-muted)]">
              {transition.note}
            </span>
          ) : null}
          {busy ? <Spinner label="Applying" /> : null}
        </button>
      </li>
    );
  };

  const nothingAtAll = !isLoading && menu.suggested.length === 0 && menu.others.length === 0;

  return (
    <div className="min-w-0">
      <p className="px-3 py-2 text-[11px] tracking-wide text-[color:var(--color-ink-muted)] uppercase">
        From {currentLabel}
      </p>

      {submitError ? (
        <p className="mx-2 mb-2 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-xs text-red-900 dark:border-red-900 dark:bg-red-950/60 dark:text-red-100">
          <span className="font-semibold">{submitError.message}</span>
          {submitError.reason ? <> — {submitError.reason}</> : null}
        </p>
      ) : null}

      {isLoading ? (
        <p className="flex items-center gap-2 px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
          <Spinner label="Loading suggested transitions" /> Loading the suggested moves…
        </p>
      ) : null}

      {loadError ? (
        <div className="px-3 py-2">
          {/*
            ⭐⭐ "THE SUGGESTIONS", NOT "THE PERMITTED MOVES". Since PH-19 the failure of this request
            costs the reader a HINT, not a capability — the other statuses below are legal whether or
            not the server managed to tell us what usually comes next, so they are still offered.
            ⛔ It used to render NOTHING here and say "Could not load the permitted moves", which
            turned a hint being unavailable into the ticket being stuck.
          */}
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
            Could not load the suggested moves
          </p>
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
            {loadError.reason ?? loadError.message}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded border border-[color:var(--color-line)] px-2 py-1 text-xs font-semibold"
          >
            Retry
          </button>
        </div>
      ) : null}

      {menu.suggested.length > 0 ? (
        <>
          <p className="px-3 pt-1 text-[10px] font-bold tracking-wide text-[color:var(--color-ink-muted)] uppercase">
            Suggested next
          </p>
          <ul className="pb-1">{menu.suggested.map(option)}</ul>
        </>
      ) : null}

      {menu.others.length > 0 ? (
        <>
          <p className="border-t border-[color:var(--color-line)] px-3 pt-2 text-[10px] font-bold tracking-wide text-[color:var(--color-ink-muted)] uppercase">
            {menu.suggested.length > 0 ? 'Any other status' : 'Any status'}
          </p>
          {/* ⭐ Said out loud, once, where somebody will read it: the graph is not a fence. */}
          <p className="px-3 pb-1 text-[11px] text-[color:var(--color-ink-muted)]">
            A ticket may move to any active status — including back.
          </p>
          <ul className="pb-1">{menu.others.map(option)}</ul>
        </>
      ) : null}

      {nothingAtAll ? (
        /*
          ⛔ IT NO LONGER SAYS "No moves are permitted from Done. An admin can add one in Settings →
          Transitions." That sentence was the reason a done ticket could not be reopened anywhere in
          this product, and it is false: `done` has no outgoing edges in `status_transition` and any
          active status is still a legal target. If nothing is on offer here, the VOCABULARY is what
          is missing — not permission.
        */
        <p className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
          No other status is available to move to. The status list could not be loaded, so there is
          nothing to offer — this is not a rule about <strong>{currentLabel}</strong>.
        </p>
      ) : null}
    </div>
  );
}
